import { MutableRefObject, useEffect, useRef, useState } from "react";
import { getActiveElement } from "../../../internal";

type HotkeyFocusParams = {
  disabled: boolean;
  hotkey: string;
  onHotkeyIn?: () => void | Promise<void>;
  onHotkeyOut?: () => void | Promise<void>;
  refToFocus: MutableRefObject<HTMLElement>;
  rootRef: MutableRefObject<HTMLElement>;
};

/**
 * A hook that listens for a hotkey press and focuses an element when the hotkey is pressed.
 * The hook also listens for the Escape key to blur the element and refocus the previous element.
 *
 * @param params
 * @param params.disabled - If true, the hotkey will not be active
 * @param params.hotkey - The key to press to focus the element
 * @param params.onHotkeyIn - A function to run when the hotkey is pressed; this runs before the element is focused
 * @param params.onHotkeyOut - A function to run when the user presses Escape after focusing the element
 * @param params.refToFocus - The ref to the element that will be focused when the hotkey is pressed
 * @param params.rootRef - The ref to the element that will listen for changes to focus, disabling the Escape key
 */
export default function useHotkeyFocus({
  disabled,
  hotkey,
  onHotkeyIn,
  onHotkeyOut,
  refToFocus,
  rootRef,
}: HotkeyFocusParams) {
  const [shouldEscapeRefocus, setShouldEscapeRefocus] = useState(false);
  const previousFocusElement = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (disabled || typeof document === "undefined") return;
    const rootElement = rootRef.current;

    const blurHandler = (e: FocusEvent) => {
      if (e.relatedTarget && rootElement.contains(e.relatedTarget as Node)) {
        return;
      }
      // If the user's focus leaves the area, pressing Escape will no longer refocus the previous element
      setShouldEscapeRefocus(false);
    };

    const keyDownHandler = async (e: KeyboardEvent) => {
      const activeElement = getActiveElement(document);

      if (
        activeElement?.tagName.toLowerCase() !== "input" &&
        activeElement?.tagName.toLowerCase() !== "textarea" &&
        activeElement?.tagName.toLowerCase() !== "pre" &&
        e.key === hotkey
      ) {
        e.preventDefault();
        previousFocusElement.current = activeElement as HTMLElement | null;
        await onHotkeyIn?.();

        if (refToFocus.current) {
          refToFocus.current.focus();
          setShouldEscapeRefocus(true);
          if (refToFocus.current.scrollTo) {
            refToFocus.current.scrollTo();
          }
        }
      } else if (e.code === "Escape" && shouldEscapeRefocus) {
        onHotkeyOut?.();
        if (
          previousFocusElement.current &&
          previousFocusElement.current.tagName.toLowerCase() !== "body"
        ) {
          (previousFocusElement.current as HTMLElement).focus();
        } else {
          (activeElement as HTMLElement).blur();
        }
      }
    };

    document.addEventListener("keydown", keyDownHandler);
    rootElement.addEventListener("focusout", blurHandler);

    return () => {
      document.removeEventListener("keydown", keyDownHandler);
      rootElement.removeEventListener("focusout", blurHandler);
    };
  }, [
    hotkey,
    disabled,
    rootRef,
    shouldEscapeRefocus,
    onHotkeyIn,
    refToFocus,
    onHotkeyOut,
  ]);

  return { returnFocusRef: previousFocusElement };
}
